import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import AvailabilityCalendar from '../../AvailabilityCalendar';
import { Button, EButtonType, Input } from '@repo/apg-ui';
import { ReactComponent as Edit } from '../../../assets/icons/edit.svg';
import { getStartDateFromProduct } from '../../../utils/product';
import { getDatesDiffDays } from '../../../utils/date';
import { createDateRange, parseToApiDate } from '../../../utils/react';
import { getDay, getISOWeek, getISOWeekYear, lastDayOfYear } from 'date-fns';
import * as _actionsBasket from '../../../actions/basket';
import * as _actionsJourney2 from '../../../actions/journey2';
import { numberWithSpaces } from '../../../utils/numbers';
import { SchemaPossibleBookingDates, SchemaProduct } from '@repo/legacy-commerce-api';
import { PartiallyIntegratedUserDataEC } from '../../../reducers/app';

interface Props {
  intl: any;
  location: any;
  productsJ2ByUrn: Record<string, SchemaProduct>;
  possibleBookingDates: SchemaPossibleBookingDates;
  userDataEC: PartiallyIntegratedUserDataEC;
  actionsJourney2: typeof _actionsJourney2;
  actionsBasket: typeof _actionsBasket;
  industry: any;
  basketInfo: any;
  setDigiflexData: any;
  defaultProduct?: any;
  handleDateChangeParent?: any;
}

const DigiflexConfiguration = ({
  possibleBookingDates,
  userDataEC,
  intl,
  actionsJourney2,
  setDigiflexData,
  defaultProduct,
  handleDateChangeParent,
}: Props) => {
  const { digiflex, ui } = userDataEC;

  const {
    product,
    budget,
    dateRange,
    duration,
    shareOfTime,
    dailyRepetitions,
    totalTransmission,
    dailyTransmission,
    spotLength,
    timeslot,
    startDate,
    isProductInBasket,
    invalidDaysInRange,
  } = digiflex;

  const setIsLoading = (value) => setDigiflexData({ isLoading: value });

  const [isBudgetEdit, setIsBudgetEdit] = useState(false);
  const [isSpotLengthEdit, setIsSpotLengthEdit] = useState(false);
  const [availabilities, setAvailabilities] = useState([]);

  const [previousBudget, setPrevBudget] = useState(0);
  const [errorNotices, setErrorNotices] = useState([]);
  const [isBudgetChanged, setIsBudgetChanged] = useState(false);

  const [isShowCrossYearError, setIsShowCrossYearError] = useState(false);
  const [isDropdownSpotLength, setIsDropdownSpotLength] = useState(false);
  const [maxSpotLength, setMaxSpotLength] = useState(100);
  const [allowedSpotLengths, setAllowedSpotLengths] = useState([]);

  const [isDateAdjusted, setIsDateAdjusted] = useState(false);

  const MAX_AMOUNT_OF_BOOKABLE_DAYS = 70;

  const secondsLabel = intl.formatMessage({
    id: 'ec.modals.digiflex.seconds',
    defaultMessage: 'Sekunden',
  });

  useEffect(() => {
    let initData = {};
    const initialProduct = product || defaultProduct;
    let startDateProductNew;

    if (initialProduct) {
      setIsLoading(true);
      let initBudget;
      let targetSpotLength = 10;
      if (isProductInBasket) {
        const initDailyReps = initialProduct.basketdailyrepetition !== 0 || initialProduct.dailyrepetition;
        initBudget =
          initialProduct.pricefordates?.length > 0 ? initialProduct.pricefordates[0].price : initialProduct.basketprice;
        targetSpotLength = initialProduct.basketspotlength
          ? initialProduct.basketspotlength
          : initialProduct.spotlength;

        initData['timeslotInfo'] = initialProduct.timeslotsinformation?.filter(
          (id) => id.timeslot === initialProduct.baskettimeslot,
        )[0];
        initData['timeslot'] = initialProduct.baskettimeslot;
        initData['dailytransmission'] = initialProduct.basketdailytransmission;
        initData['totaltransmission'] = initialProduct.baskettotaltransmission;
        initData['dailyrepetition'] = initDailyReps;
        initData['shareoftime'] = initialProduct.basketshareoftime;
        startDateProductNew = getStartDateFromProduct(initialProduct, initialProduct);
        if (typeof startDateProductNew === 'number') {
          startDateProductNew = new Date(startDateProductNew);
        }
      } else {
        const initTotalTrans =
          initialProduct.pricefordates?.length > 0
            ? initialProduct.pricefordates[0].totaltransmission
            : initialProduct?.weeklytransmission;
        const initDailyTrans =
          initialProduct.pricefordates?.length > 0
            ? initialProduct.pricefordates[0].dailytransmission
            : initialProduct?.dailytransmission;
        initBudget =
          initialProduct.pricefordates?.length > 0
            ? initialProduct.pricefordates[0].price
            : initialProduct.standardprice;
        if (initialProduct.spotlength) {
          targetSpotLength = initialProduct.spotlength;
        }
        initData['timeslot'] = initialProduct.defaulttimeslot;
        initData['timeslotInfo'] = initialProduct.timeslotsinformation?.filter(
          (id) => id.timeslot === initialProduct.defaulttimeslot,
        )[0];
        initData['dailytransmission'] = initDailyTrans;
        initData['totaltransmission'] = initTotalTrans;
        initData['dailyrepetition'] = initialProduct?.dailyrepetition;
      }
      setMaxSpotLength(initialProduct.maxspotlength);
      initData['spotLength'] = targetSpotLength;
      initData['budget'] = initBudget;
      initData['spotLength'] = targetSpotLength;
      if (initialProduct.allowedspotlengths) {
        setIsDropdownSpotLength(true);
        setAllowedSpotLengths(
          initialProduct.allowedspotlengths.map((item) => {
            return { id: item, value: item, dropdownLabel: item };
          }),
        );
      }

      const date =
        userDataEC.startDate ||
        (possibleBookingDates && possibleBookingDates.startdigital
          ? new Date(possibleBookingDates.startdigital.date)
          : new Date());
      let durationState = initialProduct.duration || userDataEC.duration || 7;

      initData['duration'] = durationState;
      const startProduct = startDateProductNew || new Date(date);
      const endDate = initialProduct.endDate;

      if (startProduct && endDate) {
        durationState = getDatesDiffDays(endDate, startProduct);
      }

      initData['startDate'] = startProduct;
      initData['dateRange'] = [
        createDateRange(startProduct, durationState, {
          toEndOfYear: product.duration > 28,
          fullWeeks: false,
        }),
      ];

      setDigiflexData({
        ...initData,
        initData: initData,
        isLoading: false,
      });

      getNewPrice(initData['timeslot'], durationState, initData['dateRange'][0]);
    }
  }, [possibleBookingDates]);

  useEffect(() => {
    if (possibleBookingDates) {
      updateAvailabilities(userDataEC.startDate);
      // @ts-expect-error can't compare string and Date here..
      if (userDataEC.startDate < new Date(possibleBookingDates.startdigital.date) && !isProductInBasket) {
        const startDate = new Date(possibleBookingDates.startdigital.date);
        const range = createDateRange(startDate, product.duration || userDataEC.duration || 7, {
          toEndOfYear: product.duration > 28,
          fullWeeks: false,
        });
        setDigiflexData({ startDate: new Date(possibleBookingDates.startdigital.date), dateRange: [range] });
      }
    }
  }, [possibleBookingDates]);

  const getDaysArray = function (start, end) {
    for (var arr = [], dt = new Date(start); dt <= new Date(end); dt.setDate(dt.getDate() + 1)) {
      arr.push(new Date(dt));
    }
    return arr;
  };

  const updateAvailabilities = (startDate, _shareOfTime?, _timeslot?, _range?) => {
    const sot = _shareOfTime ? _shareOfTime : shareOfTime || 100;
    const ts = _timeslot ? _timeslot : timeslot;
    const targetRange = _range || (dateRange && dateRange[0]);
    const diffFromPossibleStart = getDatesDiffDays(
      new Date(possibleBookingDates.startdigital.date),
      new Date(startDate),
    );
    product &&
      spotLength &&
      sot &&
      actionsJourney2
        .getAvailabilityForDigitalProduct({
          producturn: product.producturn,
          startdate: parseToApiDate(possibleBookingDates.startdigital.date),
          duration: 62 + diffFromPossibleStart,
          timeslot: ts,
          spotlength: spotLength,
          // @ts-expect-error does not exist on type:
          shareoftime: sot,
        })
        .then((res) => {
          if (res && res.data) {
            setAvailabilities(res.data.availability);
            if (res && res.notices.length > 0) {
              checkNotices(res.notices, errorNotices);
            }
            let newInvalidDays = [];
            if (targetRange) {
              for (let day of getDaysArray(targetRange.startDate, targetRange.endDate)) {
                const dayIndex = getDay(day) == 0 ? 7 : getDay(day) + 1;
                const weekNo = getISOWeek(day);
                const year = getISOWeekYear(day);

                // eslint-disable-next-line no-loop-func
                res.data.availability.forEach((availabilityObj) => {
                  if (
                    availabilityObj.day == dayIndex &&
                    weekNo == availabilityObj.week &&
                    availabilityObj.year == year &&
                    availabilityObj.rating < 0
                  ) {
                    newInvalidDays.push(day);
                  }
                });
              }
              setDigiflexData({
                invalidDaysInRange: newInvalidDays,
              });
            }
          }
        });
  };

  const handleDateChange = (date, item?, _timeslot?) => {
    let newDuration;
    setIsLoading(true);
    setIsDateAdjusted(false);

    if (item && item.selection) {
      newDuration = getDatesDiffDays(item.selection.endDate, date) + 1;
    } else {
      newDuration = duration;
    }
    if (newDuration > MAX_AMOUNT_OF_BOOKABLE_DAYS) {
      newDuration = MAX_AMOUNT_OF_BOOKABLE_DAYS;
    }

    const currentTimeslot = _timeslot || timeslot;

    if ([3, 5, 11, 12, 13, 14].includes(currentTimeslot)) {
      if (newDuration < 7) {
        newDuration = 7;
        setIsDateAdjusted(true);
      } else if (newDuration % 7 !== 0) {
        newDuration = newDuration - (newDuration % 7);
        if (newDuration != MAX_AMOUNT_OF_BOOKABLE_DAYS) {
          newDuration = newDuration + 7;
        }
      }
    }

    if (product.mindays && newDuration < product.mindays) {
      newDuration = product.mindays;
      setIsDateAdjusted(true);
    }

    if (product.maxdays && newDuration > product.maxdays) {
      newDuration = product.maxdays;
    }

    if (currentTimeslot === 5) {
      const dayIndex = getDay(date) == 0 ? 7 : getDay(date);
      if (dayIndex !== 0) {
        date.setDate(date.getDate() - dayIndex + 1);
        const currentNotices = errorNotices;
        currentNotices.push(
          intl.formatMessage({
            id: 'ec.modals.digiflex.onlyMonday.notice',
            defaultMessage: 'Das Startdatum wurde auf einen Montag geändert.',
          }),
        );
        setErrorNotices(currentNotices);
      }
    }
    const range = createDateRange(date, newDuration, {
      toEndOfYear: false,
      fullWeeks: false,
    });

    if (getISOWeekYear(range.startDate) !== getISOWeekYear(range.endDate)) {
      range.endDate = lastDayOfYear(range.startDate);
      newDuration = getDatesDiffDays(range.startDate, range.endDate);
      setIsShowCrossYearError(true);
    } else {
      setIsShowCrossYearError(false);
    }
    setDigiflexData({
      dateRange: [range],
      startDate: date,
      duration: newDuration,
    });

    if (handleDateChangeParent) {
      handleDateChangeParent(date, Math.ceil(newDuration / 7) * 7);
    }

    if (isBudgetChanged) {
      actionsJourney2
        .getShareOfTime({
          producturn: product.producturn,
          timeslot: currentTimeslot,
          duration: newDuration,
          startdate: parseToApiDate(date),
          spotlenght: spotLength,
          budget: budget,
        })
        .then((res) => {
          let notices = errorNotices;
          if (res && res.notices.length > 0) {
            notices = checkNotices(res.notices, []);
          } else {
            setErrorNotices([]);
          }
          getNewPrice(currentTimeslot, newDuration, range, res.data.shareoftime, notices);
        });
    } else {
      getNewPrice(currentTimeslot, newDuration, range);
    }
  };

  const getNewPrice = (_timeslot, _duration?, _range?, _shareOfTime?, updatedNotices?, isForceCleanNotices?) => {
    const targetRange = _range ? _range : dateRange[0];
    actionsJourney2
      .getPriceForProductNoStateUpdate({
        producturn: product.producturn,
        startdate: targetRange.startDate ? parseToApiDate(targetRange.startDate) : parseToApiDate(startDate),
        timeslot: _timeslot,
        duration: _duration ? _duration : duration,
        spotlength: spotLength,
        shareoftime: _shareOfTime || shareOfTime,
      })
      .then((res) => {
        const newProduct = { ...product };
        newProduct.pricefordates = res.data.prices;
        if (res.data.prices.length > 0) {
          setDigiflexData({
            dailyTransmission: res.data.prices[0].dailytransmission,
            totalTransmission: res.data.prices[0].totaltransmission,
            dailyRepetitions: res.data.prices[0].dailyrepetition,
          });
        } else {
          setDigiflexData({
            dailyTransmission: 0,
            totalTransmission: 0,
            dailyRepetitions: 0,
          });
        }
        if (res && res.notices.length > 0) {
          checkNotices(res.notices, updatedNotices || errorNotices);
        } else {
          if (isForceCleanNotices) {
            setErrorNotices([]);
          }
        }
        setDigiflexData({
          budget: res.data.prices[0].price,
          shareOfTime: res.data.prices[0].shareoftime,
          product: newProduct,
        });
        updateAvailabilities(startDate, res.data.prices[0].shareoftime, _timeslot, targetRange);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
      });
  };

  const updateSpotLength = () => {
    setIsLoading(true);
    if (isBudgetChanged) {
      actionsJourney2
        .getShareOfTime({
          producturn: product.producturn,
          timeslot: timeslot,
          duration: duration,
          startdate: parseToApiDate(startDate),
          spotlenght: spotLength,
          budget: budget,
        })
        .then((res) => {
          let notices = errorNotices;
          if (res && res.notices.length > 0) {
            notices = checkNotices(res.notices, []);
          } else {
            setErrorNotices([]);
          }
          getNewPrice(timeslot, duration, dateRange[0], res.data.shareoftime, notices);
        });
    } else {
      getNewPrice(timeslot);
    }
    setIsSpotLengthEdit(false);
  };

  const updateTimeslot = (_timeslot) => {
    setDigiflexData({
      isLoading: true,
      timeslot: _timeslot,
      timeslotInfo: product.timeslotsinformation.filter((id) => id.timeslot === _timeslot)[0],
    });
    handleDateChange(startDate, undefined, _timeslot);
  };

  const checkNotices = (notices, oldNotices) => {
    let newNotices = oldNotices;
    for (let notice of notices) {
      if (notice.code === 2111) {
        setDigiflexData({
          budget: notice.value,
        });
        newNotices.push(notice.message);
      } else if (notice.code === 2112 || notice.code === 1021) {
        setDigiflexData({
          spotLength: notice.value,
        });
        newNotices.push(notice.message);
      } else if (notice.code === 2102 || notice.code === 1022) {
        newNotices.push(notice.message);
        setDigiflexData({
          duration: notice.value,
        });
      } else if (notice.code === 2101) {
        const newStartDate = new Date(startDate);
        newStartDate.setDate(startDate.getDate() - notice.value);
        setDigiflexData({
          startDate: newStartDate,
        });
        newNotices.push(notice.message);
      }
    }
    setErrorNotices(newNotices);
    return newNotices;
  };

  const updateBudget = () => {
    if (budget !== previousBudget) {
      setIsBudgetChanged(true);
    }
    setIsLoading(true);
    actionsJourney2
      .getShareOfTime({
        producturn: product.producturn,
        timeslot: timeslot,
        duration: duration,
        startdate: parseToApiDate(startDate),
        spotlenght: spotLength,
        budget: budget,
      })
      .then((res) => {
        if (res && res.notices.length > 0) {
          checkNotices(res.notices, []);
        } else {
          setErrorNotices([]);
        }
        setDigiflexData({
          dailyTransmission: res.data.dailytransmission,
          totalTransmission: res.data.totaltransmission,
          dailyRepetitions: res.data.dailyrepetition,
          shareOfTime: res.data.shareoftime,
        });
        setIsBudgetEdit(false);
        updateAvailabilities(startDate, res.data.shareoftime);
        setIsLoading(false);
      });
  };

  return (
    <div className='digiflex'>
      {errorNotices.length > 0 &&
        errorNotices.map((notice, idx) => (
          <div key={`notices-${idx}`} className='py-3 px-24 font-14 bg-yellow varied-warning mb-3'>
            <b className='me-2'>
              <FormattedMessage id='ec.modals.digiflex.note' defaultMessage='Note' />
            </b>
            {notice}
          </div>
        ))}
      {((timeslot && timeslot !== 1) || isDateAdjusted) && (
        <div className='py-2 px-4 font-14 bg-yellow varied-warning mb-2'>
          <b className='me-1'>
            <FormattedMessage id='ec.modals.digiflex.note' defaultMessage='Note' />
          </b>
          <FormattedMessage
            id='ec.modals.digiflex.timeslotCustom.notice'
            defaultMessage='Bitte mindestens {days} Tage auswählen'
            values={{ days: product && product.mindays }}
          />
        </div>
      )}
      {isShowCrossYearError && (
        <div className='py-3 px-24 font-14 bg-yellow varied-warning mb-3'>
          <b className='me-2'>
            <FormattedMessage id='ec.modals.digiflex.note' defaultMessage='Note' />
          </b>
          <FormattedMessage
            id='ec.modals.digiflex.timeslotweekly.crossYearError'
            defaultMessage='Sie haben eine jahresübergreifende Zeitspanne ausgewählt.
                                          Dies ist aktuell so nicht möglich. Bitte wählen Sie eine andere Zeitspanne aus.'
          />
        </div>
      )}
      <div className='timeslot-tabs d-flex bg-white'>
        {product.timeslotsinformation &&
          Object.keys(product.timeslotsinformation).map((_timeslotId, idx) => (
            <div
              className={classNames('slot p-4', {
                selected: timeslot === product.timeslotsinformation[_timeslotId].timeslot,
              })}
              key={`slot-${idx}`}
              onClick={() => {
                updateTimeslot(product.timeslotsinformation[_timeslotId].timeslot);
              }}
            >
              <div className='font-14 font-weight-bold'>{product.timeslotsinformation[_timeslotId].name}</div>
              <div className='font-12 pt-2'>{product.timeslotsinformation[_timeslotId].timespan}</div>
            </div>
          ))}
      </div>
      <AvailabilityCalendar
        handleDateChange={handleDateChange}
        availabilities={availabilities}
        possibleBookingDates={possibleBookingDates}
        dateRange={dateRange}
        onShownDateChange={(date) => {
          updateAvailabilities(date, shareOfTime, timeslot);
        }}
        duration={duration}
        intl={intl}
        isMobile={ui.isMobile}
        isAnalogProduct={false}
      />
      {(invalidDaysInRange.length > 0 || product.state !== 0) && (
        <div className='py-3 px-24 my-4 font-14 bg-yellow varied-warning'>
          <b className='me-2'>
            <FormattedMessage id='ec.modals.digiflex.variedAvailability' defaultMessage='Hinweis:' />
          </b>
          <FormattedMessage
            id='ec.modals.digiflex.variedAvailabilityText'
            defaultMessage='Nicht jeder Tag im ausgewählten Zeitraum ist verfügbar. Bitte überprüfen Sie Ihre Auswahl.'
          />
        </div>
      )}
      <div
        className={classNames('d-flex w-100 p-24 budgetInfo', {
          'flex-column': ui.isMobile,
        })}
      >
        <div
          className={classNames('d-flex flex-column', {
            'w-30': !ui.isMobile,
            'w-100 p-4': ui.isMobile,
          })}
        >
          <div className='font-16 font-weight-bold'>
            <FormattedMessage id='ec.modals.digiflex.budget' defaultMessage='Budget' />
          </div>

          {!isBudgetEdit && (
            <div className='d-flex mt-3 flex-column align-items-start'>
              <div className='font-14 mt-2'>
                {numberWithSpaces(budget)} CHF {isBudgetChanged && <span>*</span>}
              </div>
              <Button
                buttonType={EButtonType.BasicLink}
                className='mt-0'
                onClick={() => {
                  setIsBudgetEdit(true);
                  setPrevBudget(budget);
                }}
              >
                <Edit className='editIcon' /> Edit
              </Button>
            </div>
          )}

          {isBudgetEdit && (
            <div className='d-flex flex-column'>
              <Input
                value={budget}
                name={'budget'}
                onChange={(name, value) => {
                  setDigiflexData({ budget: value });
                }}
                type='number'
                placeholder={intl.formatMessage({
                  id: 'ec.digiflex.budget',
                  defaultMessage: 'Budget',
                })}
                inputWrapperClass='mt-0 pe-24 my-2'
              >
                <div className='font-14 font-weight-bold currencyLabel'>CHF</div>
              </Input>

              <div className='d-flex mt-2'>
                <Button
                  buttonType={EButtonType.InvertedPillMedium}
                  className='mt-0'
                  isDisabled={!budget}
                  onClick={updateBudget}
                >
                  <FormattedMessage id='ec.components.buttons.save' defaultMessage='Speichern' />
                </Button>
                <Button
                  buttonType={EButtonType.BasicLink}
                  className='mt-0 ms-4'
                  onClick={() => {
                    setIsBudgetEdit(false);
                  }}
                >
                  <FormattedMessage id='ec.components.buttons.cancel' defaultMessage='Abbrechen' />
                </Button>
              </div>
            </div>
          )}
          {isBudgetChanged && (
            <div className='py-3 px-24 font-12 bg-yellow varied-warning me-24 mt-4'>
              <b className='me-2'>
                <FormattedMessage id='ec.modals.digiflex.variedAvailability' defaultMessage='Hinweis:' />
              </b>
              <FormattedMessage
                id='ec.modals.digiflex.budgedChangedNotice'
                defaultMessage='Das Budget kann auf der Kassenseite wegen Rundungsdifferenzen leicht abweichen'
              />
            </div>
          )}
        </div>
        <div
          className={classNames('d-flex flex-column ', {
            'w-33 px-48 border-left border-right': !ui.isMobile,
            'w-100 p-4': ui.isMobile,
          })}
        >
          <div className='font-16 font-weight-bold'>
            <FormattedMessage id='ec.modals.digiflex.spotlength' defaultMessage='Spotlänge' />
          </div>
          {!isSpotLengthEdit && (
            <div className='d-flex mt-3 flex-column align-items-start'>
              <div className='font-14 mt-2'>
                {spotLength} {secondsLabel}
              </div>
              <Button buttonType={EButtonType.BasicLink} className='mt-0' onClick={() => setIsSpotLengthEdit(true)}>
                <Edit className='editIcon' /> Edit
              </Button>
            </div>
          )}
          {isSpotLengthEdit && (
            <div className='d-flex flex-column'>
              {isDropdownSpotLength ? (
                <Input
                  id='spotLength'
                  name='spotLength'
                  inputWrapperStyle={{
                    width: '100%',
                    maxWidth: '100%',
                    paddingLeft: 0,
                    paddingRight: 0,
                  }}
                  label='spotLength'
                  clearable={false}
                  onSelected={(name, value) => {
                    setDigiflexData({ spotLength: value });
                  }}
                  value={spotLength}
                  disabled={allowedSpotLengths.length <= 1}
                  readOnly={allowedSpotLengths.length <= 1}
                  selectOptions={allowedSpotLengths}
                  type='text'
                  inputWrapperClass={`col-4 ms-0 mb-4 mt-2 align-self-start`}
                />
              ) : (
                <Input
                  id='spotLength'
                  name='spotLength'
                  inputWrapperStyle={{
                    width: '100%',
                    maxWidth: '100%',
                    paddingLeft: 0,
                    paddingRight: 0,
                  }}
                  label='spotLength'
                  clearable={false}
                  onChange={(name, value) => {
                    setDigiflexData({ spotLength: value });
                  }}
                  value={spotLength}
                  // @ts-ignore
                  step={1}
                  min={1}
                  max={maxSpotLength}
                  type='number'
                  inputWrapperClass={`col-4 ms-0 mb-4 mt-2 align-self-start`}
                />
              )}
              {isDropdownSpotLength && allowedSpotLengths.length <= 1 && (
                <div className='py-3 px-24 font-12 bg-yellow mb-4 varied-warning'>
                  <b className='me-2'>
                    <FormattedMessage id='ec.modals.digiflex.variedAvailability' defaultMessage='Hinweis:' />
                  </b>
                  <FormattedMessage
                    id='ec.modals.digiflex.onlyOneSpotLengthNotice'
                    defaultMessage='Dieses Produkt kann jeweils pro Kampagne nur mit {seconds} Sekunden gebucht werden.'
                    values={{
                      seconds: allowedSpotLengths ? allowedSpotLengths[0] && 15 : 15,
                    }}
                  />
                </div>
              )}
              <div className='d-flex mt-2'>
                <Button
                  buttonType={EButtonType.InvertedPillMedium}
                  className='mt-0'
                  isDisabled={!budget}
                  onClick={updateSpotLength}
                >
                  <FormattedMessage id='ec.components.buttons.save' defaultMessage='Speichern' />
                </Button>
                <Button
                  buttonType={EButtonType.BasicLink}
                  className='mt-0 ms-4'
                  onClick={() => {
                    setIsSpotLengthEdit(false);
                  }}
                >
                  <FormattedMessage id='ec.components.buttons.cancel' defaultMessage='Abbrechen' />
                </Button>
              </div>
            </div>
          )}
        </div>
        <div
          className={classNames('d-flex flex-column', {
            'w-40 ps-48': !ui.isMobile,
            'w-100 p-4': ui.isMobile,
          })}
        >
          <div className='font-16 font-weight-bold'>
            <FormattedMessage id='ec.modals.digiflex.broadcasts' defaultMessage='Ausstrahlungen' />
          </div>
          {dailyTransmission !== null && dailyTransmission !== 0 && (
            <div className='font-14 mt-3'>
              <b className='me-3'>
                <FormattedMessage id='ec.modals.digiflex.dailyBroadcasts' defaultMessage='Tägliche Ausstrahlungen' />:
              </b>
              {numberWithSpaces(dailyTransmission)}
            </div>
          )}
          {totalTransmission !== null && totalTransmission !== 0 && (
            <div className='font-14'>
              <b className='me-3'>
                <FormattedMessage id='ec.modals.digiflex.weeklyBroadcasts' defaultMessage='Total Ausstrahlungen' />:
              </b>
              {numberWithSpaces(totalTransmission)}
            </div>
          )}
          {dailyRepetitions !== 0 && dailyRepetitions !== null && (
            <div className='font-14'>
              <span className='me-3'>
                <FormattedMessage
                  id='ec.general.digiflexRepetitions'
                  defaultMessage='{repsHeader}: ca. alle {repetitions} Sekunden'
                  values={{
                    repetitions: numberWithSpaces(dailyRepetitions),
                    repsHeader: (
                      <b>
                        {intl.formatMessage({
                          id: 'ec.general.repetitions',
                          defaultMessage: 'Wiederholung',
                        })}
                      </b>
                    ),
                  }}
                />
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DigiflexConfiguration;
